import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Typography } from "@mui/material";
import SignatureCanvas from "react-signature-canvas";

import Client from "./client";

import { Field, TextInput } from "../ui/form";
import OrderChecklist from "./OrderChecklist";
import { tokens } from "../../theme";
import { IconPen } from "../ui/icons";
import { useIsMobile } from "../../hooks/useIsMobile";

const Signature = ({ formState, setFormState, isEdition, isOrderClosed, isReadOnlyMode }) => {
  const isMobile = useIsMobile();
  const signatureSize = isMobile ? 260 : 380;
  const [signature, setSignature] = useState();
  const [url, setUrl] = useState();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);



  /**
   * La firma dibujada, o null si el recuadro está vacío.
   *
   * `getTrimmedCanvas()` recorta al trazo, así que sobre un lienzo vacío
   * devuelve un PNG de 1x1 que es "truthy": un simple click sin dibujar nada
   * quedaba guardado como firma válida y el checklist la daba por hecha.
   */
  const currentSignatureOrNull = () => {
    if (!signature || signature.isEmpty?.()) return null;
    return signature.getTrimmedCanvas()?.toDataURL("image/png") || null;
  };

  const clearSignature = () => {
    if (isReadOnlyMode) return;
    setShowCanvas(true);
    signature?.clear();
    setFormState({ ...formState, signature: null, signature_aclaration: "" });
    localStorage.removeItem("signatureData");
  };
  const saveSignature = (e) => {
    if (isReadOnlyMode) return;

    const currentSignature = currentSignatureOrNull();

    if (currentSignature) setUrl(currentSignature);

    setFormState({
      ...formState,
      signature: currentSignature || formState.signature, // Mantener la firma existente si no hay nueva
      signature_aclaration: e.target.value,
    });
  };

  useEffect(() => {
    // logic to save canvas
    const canvas = canvasRef.current;
    const handleMouseDown = () => {
      setIsDrawing(true);
    };

    const handleMouseUp = () => {
      setIsDrawing(false);
      saveDrawing();
    };

    const saveDrawing = () => {
      // Sólo se guarda si hay trazo: un click al vacío no es una firma.
      let image = currentSignatureOrNull();
      if (image) {
        localStorage.setItem("signatureData", image);
        setUrl(image); // Actualizar también el estado local
        setFormState({
          ...formState,
          signature: image,
        });
      }
    };

    if (!isOrderClosed && !isReadOnlyMode) {
      canvas?.addEventListener("mousedown", handleMouseDown);
      canvas?.addEventListener("mouseup", handleMouseUp);

      return () => {
        // Clean up event listeners on component unmount
        canvas?.removeEventListener("mousedown", handleMouseDown);
        canvas?.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDrawing]);

  useEffect(() => {
    // Restore signature data when the component mounts
    const savedSignatureData = localStorage.getItem("signatureData");
    if (signature && savedSignatureData) {
      signature?.fromDataURL(savedSignatureData);
      const canvas = signature.getCanvas();
      canvas.width = signatureSize;
      canvas.height = signatureSize;
      
      // Actualizar también el estado local y formState
      setUrl(savedSignatureData);
      setFormState(prev => ({
        ...prev,
        signature: savedSignatureData
      }));
    }
  }, [signature, signatureSize]);

  const readOnly = isOrderClosed || isReadOnlyMode;
  const canvasWidth = isMobile ? 300 : 420;
  const canvasHeight = Math.round(canvasWidth * 0.55);

  return (
    <Box
      className="step_container"
      sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}
    >
      <Client
        showIcon={!isOrderClosed && !isReadOnlyMode}
        formState={formState}
        setFormState={setFormState}
        isReadOnlyMode={isReadOnlyMode}
      />

      {/* Firma y aclaración en una tarjeta: es un solo acto, no dos bloques sueltos. */}
      <Box
        sx={{
          backgroundColor: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: `${tokens.radiusCard}px`,
          padding: { xs: "16px", md: "18px 20px 20px" },
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: `${canvasWidth}px minmax(0, 1fr)` },
          gap: { xs: "16px", md: "22px" },
          alignItems: "start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, flexGrow: 1 }}>
              Firma del cliente
            </Typography>
            {!readOnly && (
              <Box
                onClick={clearSignature}
                role="button"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: 32,
                  paddingX: "6px",
                  fontSize: "12.5px",
                  fontWeight: 500,
                  color: tokens.brandDark,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Borrar y rehacer
              </Box>
            )}
          </Box>

          <Box
            ref={canvasRef}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              width: canvasWidth,
              height: canvasHeight,
              maxWidth: "100%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${tokens.borderStrong}`,
              cursor: readOnly ? "default" : "crosshair",
            }}
          >
            {formState.signature && !showCanvas ? (
              <Box
                component="img"
                src={formState.signature}
                alt="Firma del cliente"
                sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
              />
            ) : (
              <SignatureCanvas
                canvasProps={{ width: canvasWidth, height: canvasHeight }}
                ref={(data) => setSignature(data)}
              />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", color: tokens.ink3 }}>
            <IconPen size={13} />
            <Typography sx={{ fontSize: "11.5px" }}>
              {readOnly ? "Firma registrada" : "Firmá con el dedo o el mouse dentro del recuadro"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
          <Field
            label="Aclaración de la firma"
            hint="Nombre y documento de quien recibe el trabajo."
          >
            <TextInput
              value={formState.signature_aclaration || ""}
              disabled={readOnly}
              placeholder="Ej: Marcelo Vega · DNI 28.455.190"
              onChange={(e) => saveSignature(e)}
            />
          </Field>

          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <OrderChecklist formState={formState} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Signature;
